import { useId, useState } from 'react';
import type { DxmCategoryNodeDTO } from '@wberp/shared';
import { CategoryPicker } from './category-picker';
import { Modal } from './components';
import './category-brand-fields.css';

type Props = {
  subjectId?: number;
  selectedPath: DxmCategoryNodeDTO[];
  brand: string;
  disabled?: boolean;
  retry?: boolean;
  hideLabels?: boolean;
  onCategoryChange: (category: DxmCategoryNodeDTO | undefined, path: DxmCategoryNodeDTO[]) => void;
  onBrandChange: (brand: string) => void;
};

export function CategoryBrandFields({ subjectId, selectedPath, brand, disabled = false, retry = false, hideLabels = false, onCategoryChange, onBrandChange }: Props) {
  const id = useId();
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [draftCategory, setDraftCategory] = useState<DxmCategoryNodeDTO>();
  const [draftPath, setDraftPath] = useState<DxmCategoryNodeDTO[]>([]);
  const selectedCategory = selectedPath.find((category) => category.categoryId === subjectId);
  const categoryLabel = selectedCategory ? `${selectedCategory.categoryNameZh || selectedCategory.categoryName} · ${subjectId}` : subjectId ? `类目 ${subjectId}` : '点击搜索并选择类目';
  return <div className="category-brand-fields">
    <div className="category-brand-field">
      {!hideLabels && <div className="category-brand-label"><label htmlFor={`${id}-category`}>WB 类目（可选）</label>{subjectId && !disabled && <button type="button" className="copy-button" onClick={() => onCategoryChange(undefined, [])}>使用 AI 选择</button>}</div>}
      <button id={`${id}-category`} type="button" className="category-brand-category input" disabled={disabled} aria-label="选择 WB 类目" aria-haspopup="dialog" onClick={() => {
        setDraftCategory(selectedCategory); setDraftPath(selectedPath); setCategoryOpen(true);
      }}><span>{categoryLabel}</span><span aria-hidden="true">⌕</span></button>
      <span className="field-help category-brand-path">{selectedPath.length ? selectedPath.map((category) => category.categoryNameZh || category.categoryName).join(' / ') : subjectId ? `已固定类目编号 ${subjectId}` : retry ? '留空时保留任务原有类目。' : '留空时由 AI 为每件商品选择类目。'}</span>
    </div>
    <div className="category-brand-field">
      {!hideLabels && <div className="category-brand-label"><label htmlFor={`${id}-brand`}>品牌（可选）</label></div>}
      <input id={`${id}-brand`} className="input" aria-label="填写 WB 品牌" aria-describedby={`${id}-brand-warning`} disabled={disabled} placeholder={retry ? '手动填写品牌，留空沿用默认品牌' : '手动填写品牌，可留空'} value={brand} onChange={(event) => onBrandChange(event.currentTarget.value)} />
      <span className="field-help">{retry ? '留空时沿用原任务默认品牌配置。' : '留空时沿用店铺默认品牌；未设置默认品牌时不传品牌字段。'}</span>
    </div>
    <strong id={`${id}-brand-warning`} className="category-brand-warning">
      <svg className="category-brand-warning-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9" /><path d="M12 8v4m0 4h.01" /></svg>
      <span>提醒：请填写该分类下存在的品牌</span>
    </strong>
    {categoryOpen && <Modal title="搜索选择 WB 类目" description="按中文、俄文或编号搜索，选定商品对应的叶子类目。" size="large" onClose={() => setCategoryOpen(false)} actions={<><button type="button" className="button" onClick={() => setCategoryOpen(false)}>取消</button><button type="button" className="button button-primary" disabled={!draftCategory?.isLeaf} onClick={() => {
      if (!draftCategory?.isLeaf) return;
      onCategoryChange(draftCategory, draftPath); setCategoryOpen(false);
    }}>使用此类目</button></>}>
      <CategoryPicker initialOpen value={draftCategory?.categoryId} selectedPath={draftPath} onChange={(category, path) => { setDraftCategory(category); setDraftPath(path); }} />
    </Modal>}
  </div>;
}
